import { supabase } from '../lib/supabase';

// ─── Award Engine ──────────────────────────────────────────────────────────────
// Trigger constants:
//   VERSE_ADDED, SESSION_COMPLETED, SESSION_MILESTONE,
//   REFLECTION_WRITTEN, REFLECTION_MILESTONE,
//   STREAK_MILESTONE, STREAK_FREEZE_USED,
//   WEEKLY_VERSE_STREAK, WEEKLY_VERSE_STREAK_2X,
//   VERSE_MILESTONE, VERSE_MASTERED, BOOK_VARIETY,
//   READING_PLAN_COMPLETED, TAG_CREATED, TAG_MILESTONE,
//   EARLY_RISER, NIGHT_AND_DAY, SABBATH_VERSE_MILESTONE

/**
 * Ensure user_talent_balance row exists for the user.
 */
async function ensureTalentBalance(userId) {
  const { error } = await supabase?.from('user_talent_balance')?.upsert({ user_id: userId }, { onConflict: 'user_id', ignoreDuplicates: true });
  if (error) console.warn('ensureTalentBalance error:', error?.message);
}

/**
 * Grant a single award and update talent balance.
 * Returns the award object if granted, null otherwise.
 */
async function grantAward(userId, definition, timesEarned = 1) {
  try {
    // Upsert user_awards
    const { data: existing } = await supabase?.from('user_awards')?.select('id, times_earned')?.eq('user_id', userId)?.eq('award_slug', definition?.slug)?.maybeSingle();

    if (existing) {
      // Update times_earned
      await supabase?.from('user_awards')?.update({ times_earned: existing?.times_earned + 1, earned_at: new Date()?.toISOString() })?.eq('id', existing?.id);
    } else {
      await supabase?.from('user_awards')?.insert({ user_id: userId, award_slug: definition?.slug, times_earned: 1 });
    }

    // Update talent balance
    const goldDelta = definition?.gold_reward || 0;
    const silverDelta = definition?.silver_reward || 0;

    if (goldDelta > 0 || silverDelta > 0) {
      await ensureTalentBalance(userId);

      // Fetch current balance
      const { data: bal } = await supabase?.from('user_talent_balance')?.select('gold_balance, silver_balance, total_gold_earned, total_silver_earned')?.eq('user_id', userId)?.single();

      let newGold = (bal?.gold_balance || 0) + goldDelta;
      let newSilver = (bal?.silver_balance || 0) + silverDelta;
      let totalGoldEarned = (bal?.total_gold_earned || 0) + goldDelta;
      let totalSilverEarned = (bal?.total_silver_earned || 0) + silverDelta;

      // Auto-convert silver to gold when silver_balance >= 7
      const silverToGold = Math.floor(newSilver / 7);
      if (silverToGold > 0) {
        newGold += silverToGold;
        totalGoldEarned += silverToGold;
        newSilver = newSilver % 7;
      }

      await supabase?.from('user_talent_balance')?.update({
          gold_balance: newGold,
          silver_balance: newSilver,
          total_gold_earned: totalGoldEarned,
          total_silver_earned: totalSilverEarned,
          updated_at: new Date()?.toISOString()
        })?.eq('user_id', userId);
    }

    return {
      slug: definition?.slug,
      name: definition?.name,
      description: definition?.description,
      icon: definition?.icon,
      goldReward: definition?.gold_reward,
      silverReward: definition?.silver_reward,
      category: definition?.category
    };
  } catch (err) {
    console.error('grantAward error:', err);
    return null;
  }
}

/**
 * checkAndGrantAwards — main entry point for the award engine.
 * @param {string} userId
 * @param {string} triggerEvent
 * @param {object} payload  e.g. { totalVerses, totalReflections, totalTags, uniqueBooks, charCount }
 * @returns {Array} newly earned award objects for popup display
 */
export async function checkAndGrantAwards(userId, triggerEvent, payload = {}) {
  if (!userId || !triggerEvent) return [];

  try {
    // Load award definitions matching this trigger
    const { data: definitions, error: defError } = await supabase?.from('award_definitions')?.select('*')?.eq('trigger_event', triggerEvent);

    if (defError || !definitions?.length) return [];

    // Load existing user awards
    const { data: userAwards } = await supabase?.from('user_awards')?.select('award_slug, times_earned')?.eq('user_id', userId);

    const earnedMap = {};
    (userAwards || [])?.forEach(a => { earnedMap[a.award_slug] = a?.times_earned; });

    const newlyEarned = [];

    for (const def of definitions) {
      const timesEarned = earnedMap?.[def?.slug] || 0;

      // Non-repeatable: skip if already earned
      if (!def?.repeatable && timesEarned >= 1) continue;

      // Repeatable: check if we've hit the next threshold multiple
      if (def?.repeatable && def?.threshold) {
        // Determine the relevant count from payload
        let count = 0;
        switch (triggerEvent) {
          case 'TAG_MILESTONE':
            count = payload?.totalTags || 0;
            break;
          case 'SESSION_MILESTONE':
            count = payload?.totalSessions || 0;
            break;
          case 'REFLECTION_MILESTONE':
            count = payload?.totalReflections || 0;
            break;
          case 'VERSE_MILESTONE':
            count = payload?.totalVerses || 0;
            break;
          case 'READING_PLAN_COMPLETED':
            count = payload?.totalPlans || 0;
            break;
          default:
            count = payload?.count || 0;
        }
        const expectedTimes = Math.floor(count / def?.threshold);
        if (expectedTimes <= timesEarned) continue;
      }

      // Non-repeatable threshold check
      if (!def?.repeatable && def?.threshold) {
        let count = 0;
        switch (triggerEvent) {
          case 'VERSE_ADDED': case'VERSE_MILESTONE':
            count = payload?.totalVerses || 0;
            break;
          case 'SESSION_COMPLETED': case'SESSION_MILESTONE':
            // BUG 2: For accuracy-gated awards (scripture_scholar, perfect_practice),
            // use accuracy from payload instead of session count
            if (def?.slug === 'scripture_scholar') {
              count = (payload?.accuracy !== undefined && payload?.accuracy >= 90) ? 1 : 0;
            } else if (def?.slug === 'perfect_practice') {
              count = (payload?.accuracy !== undefined && payload?.accuracy >= 100) ? 1 : 0;
            } else {
              count = payload?.totalSessions || 0;
            }
            break;
          case 'REFLECTION_WRITTEN': case'REFLECTION_MILESTONE':
            count = payload?.totalReflections || 0;
            if (triggerEvent === 'REFLECTION_WRITTEN' && def?.threshold === 1) {
              // First reflection — also check char count
              count = (payload?.charCount || 0) >= 200 ? 1 : 0;
            }
            break;
          case 'BOOK_VARIETY':
            count = payload?.uniqueBooks || 0;
            break;
          case 'TAG_CREATED':
            count = 1;
            break;
          case 'READING_PLAN_COMPLETED':
            count = payload?.totalPlans || 0;
            break;
          case 'EARLY_RISER':
            // threshold in DB is the hour cutoff (8 = before 8am)
            // count = startHour; award fires when startHour < threshold
            count = (payload?.startHour !== undefined) ? payload?.startHour : 99;
            break;
          case 'VERSE_MASTERED':
            count = payload?.phase || 0;
            break;
          default:
            count = 1;
        }
        if (count < def?.threshold) continue;
      }

      // Grant the award
      const awarded = await grantAward(userId, def);
      if (awarded) newlyEarned?.push(awarded);
    }

    return newlyEarned;
  } catch (err) {
    console.error('checkAndGrantAwards error:', err);
    return [];
  }
}

/**
 * checkWeeklyVerseStreak — checks if user has added at least 1 Phase 1 verse
 * in each of the last N consecutive weeks, and fires WEEKLY_VERSE_STREAK (10 weeks)
 * and WEEKLY_VERSE_STREAK_2X (10 weeks with 2+ verses/week) awards.
 * Call this on every verse add.
 * @param {string} userId
 * @param {object} supabaseClient  — pass the supabase client to avoid circular imports
 * @returns {Array} newly earned award objects
 */
export async function checkWeeklyVerseStreak(userId, supabaseClient) {
  if (!userId || !supabaseClient) return [];

  try {
    // Fetch all Phase 1+ verses with their creation date
    const { data: verses, error } = await supabaseClient
      ?.from('verses')
      ?.select('created_at')
      ?.eq('user_id', userId)
      ?.gte('current_phase', 1)
      ?.order('created_at', { ascending: true });

    if (error || !verses?.length) return [];

    // Group verses by ISO week (Monday-based)
    const getISOWeekKey = (dateStr) => {
      const d = new Date(dateStr);
      // Get Monday of the week
      const day = d?.getUTCDay(); // 0=Sun, 1=Mon...
      const diff = (day === 0 ? -6 : 1 - day);
      const monday = new Date(d);
      monday?.setUTCDate(d?.getUTCDate() + diff);
      return `${monday?.getUTCFullYear()}-${String(monday?.getUTCMonth() + 1)?.padStart(2, '0')}-${String(monday?.getUTCDate())?.padStart(2, '0')}`;
    };

    const weekMap = {};
    for (const v of verses) {
      const key = getISOWeekKey(v?.created_at);
      weekMap[key] = (weekMap?.[key] || 0) + 1;
    }

    // Sort week keys chronologically
    const sortedWeeks = Object.keys(weekMap)?.sort();

    // Count consecutive weeks ending at the most recent week
    let consecutiveWeeks = 0;
    let doubleWeeks = 0; // weeks with 2+ verses

    // Walk backwards from the most recent week
    const today = new Date();
    const currentWeekKey = getISOWeekKey(today?.toISOString());

    // Build a set of all weeks that have at least 1 verse
    const weekSet = new Set(sortedWeeks);

    // Count consecutive weeks going back from current week
    let checkDate = new Date(today);
    for (let i = 0; i < 52; i++) {
      const weekKey = getISOWeekKey(checkDate?.toISOString());
      if (weekSet?.has(weekKey)) {
        consecutiveWeeks++;
        if ((weekMap?.[weekKey] || 0) >= 2) doubleWeeks++;
        // Move back 7 days
        checkDate?.setUTCDate(checkDate?.getUTCDate() - 7);
      } else {
        break; // streak broken
      }
    }

    const newlyEarned = [];

    // WEEKLY_VERSE_STREAK: 10 consecutive weeks with at least 1 new Phase 1 verse
    if (consecutiveWeeks >= 10) {
      const awards = await checkAndGrantAwards(userId, 'WEEKLY_VERSE_STREAK', { count: consecutiveWeeks });
      newlyEarned?.push(...awards);
    }

    // WEEKLY_VERSE_STREAK_2X: 10 consecutive weeks with at least 2 new Phase 1 verses each week
    if (doubleWeeks >= 10) {
      const awards = await checkAndGrantAwards(userId, 'WEEKLY_VERSE_STREAK_2X', { count: doubleWeeks });
      newlyEarned?.push(...awards);
    }

    return newlyEarned;
  } catch (err) {
    console.error('checkWeeklyVerseStreak error:', err);
    return [];
  }
}

/**
 * updateStreakWithFreezeCheck — called on session start.
 * @returns {{ streakPreserved: boolean, goldSpent: number, newStreak: number, newAwards: Array }}
 */
export async function updateStreakWithFreezeCheck(userId) {
  if (!userId) return { streakPreserved: false, goldSpent: 0, newStreak: 1, newAwards: [] };

  try {
    const today = new Date()?.toISOString()?.split('T')?.[0];

    // Get streak data
    const { data: streak } = await supabase?.from('user_streaks')?.select('current_streak, last_session_date, freeze_gold_available')?.eq('user_id', userId)?.maybeSingle();

    if (!streak) {
      // Create streak row
      await supabase?.from('user_streaks')?.upsert({
        user_id: userId,
        current_streak: 1,
        last_session_date: today,
        freeze_gold_available: 0
      }, { onConflict: 'user_id' });
      return { streakPreserved: false, goldSpent: 0, newStreak: 1, newAwards: [] };
    }

    const lastDate = streak?.last_session_date;
    if (!lastDate) {
      await supabase?.from('user_streaks')?.update({ current_streak: 1, last_session_date: today })?.eq('user_id', userId);
      return { streakPreserved: false, goldSpent: 0, newStreak: 1, newAwards: [] };
    }

    const last = new Date(lastDate);
    const now = new Date(today);
    const missedDays = Math.floor((now - last) / (1000 * 60 * 60 * 24)) - 1;

    if (missedDays <= 0) {
      // Same day (missedDays < 0) or consecutive day (missedDays === 0)
      let newStreak = streak?.current_streak;

      if (missedDays === 0) {
        // Consecutive day — increment the streak
        newStreak = (streak?.current_streak || 0) + 1;
        await supabase?.from('user_streaks')?.update({ current_streak: newStreak, last_session_date: today })?.eq('user_id', userId);
      } else {
        // Same day — just update last_session_date, no increment
        await supabase?.from('user_streaks')?.update({ last_session_date: today })?.eq('user_id', userId);
      }

      // Check streak milestones
      const streakAwards = await checkAndGrantAwards(userId, 'STREAK_MILESTONE', { count: newStreak });
      return { streakPreserved: true, goldSpent: 0, newStreak, newAwards: streakAwards };
    }

    // Get gold balance
    const { data: bal } = await supabase?.from('user_talent_balance')?.select('gold_balance')?.eq('user_id', userId)?.maybeSingle();

    const goldBalance = bal?.gold_balance || 0;

    if (goldBalance >= missedDays) {
      // Spend gold, preserve streak
      let newGold = goldBalance - missedDays;
      await supabase?.from('user_talent_balance')?.update({ gold_balance: newGold, updated_at: new Date()?.toISOString() })?.eq('user_id', userId);
      await supabase?.from('user_streaks')?.update({ last_session_date: today })?.eq('user_id', userId);

      // Fire STREAK_FREEZE_USED award
      const freezeAwards = await checkAndGrantAwards(userId, 'STREAK_FREEZE_USED', { count: 1 });

      // Check streak milestones (streak preserved)
      const streakAwards = await checkAndGrantAwards(userId, 'STREAK_MILESTONE', { count: streak?.current_streak });

      return { streakPreserved: true, goldSpent: missedDays, newStreak: streak?.current_streak, newAwards: [...freezeAwards, ...streakAwards] };
    } else {
      // Reset streak
      await supabase?.from('user_streaks')?.update({ current_streak: 1, last_session_date: today })?.eq('user_id', userId);
      return { streakPreserved: false, goldSpent: 0, newStreak: 1, newAwards: [] };
    }
  } catch (err) {
    console.error('updateStreakWithFreezeCheck error:', err);
    return { streakPreserved: false, goldSpent: 0, newStreak: 1, newAwards: [] };
  }
}

/**
 * recordReviewSession — inserts a session record and triggers award checks.
 * @param {string} userId
 * @param {{ verse_count, correct_count, duration_seconds, start_hour, phasedVerses }} sessionData
 *   phasedVerses: optional array of { oldPhase, newPhase } from batch review results
 * @returns {Array} newly earned awards
 */
export async function recordReviewSession(userId, sessionData = {}) {
  if (!userId) return [];

  try {
    const now = new Date()?.toISOString();
    const startHour = sessionData?.start_hour ?? new Date()?.getHours();

    // Insert session record
    await supabase?.from('user_review_sessions')?.insert({
      user_id: userId,
      started_at: now,
      completed_at: now,
      verse_count: sessionData?.verse_count || 0,
      correct_count: sessionData?.correct_count || 0,
      duration_seconds: sessionData?.duration_seconds || 0,
      start_hour: startHour,
      is_complete: true
    });

    // Get total session count
    const { count: totalSessions } = await supabase?.from('user_review_sessions')?.select('id', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('is_complete', true);

    const newlyEarned = [];

    // SESSION_COMPLETED (first time)
    if (totalSessions === 1) {
      const firstSessionAwards = await checkAndGrantAwards(userId, 'SESSION_COMPLETED', { totalSessions: 1 });
      newlyEarned?.push(...firstSessionAwards);
    }

    // SESSION_MILESTONE
    const milestoneAwards = await checkAndGrantAwards(userId, 'SESSION_MILESTONE', { totalSessions });
    newlyEarned?.push(...milestoneAwards);

    // EARLY_RISER
    if (startHour < 8) {
      const earlyAwards = await checkAndGrantAwards(userId, 'EARLY_RISER', { startHour });
      newlyEarned?.push(...earlyAwards);
    }

    // NIGHT_AND_DAY — check if user also had a session after 8pm today
    const endHour = new Date()?.getHours();
    if (startHour < 8 && endHour >= 20) {
      // Same session spans early morning to late night — grant directly
      const nightAwards = await checkAndGrantAwards(userId, 'NIGHT_AND_DAY', { count: 1 });
      newlyEarned?.push(...nightAwards);
    } else if (endHour >= 20) {
      // Check if user had an early session today (before 8am)
      const todayStart = new Date();
      todayStart?.setHours(0, 0, 0, 0);
      const { count: earlyCount } = await supabase
        ?.from('user_review_sessions')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('user_id', userId)
        ?.eq('is_complete', true)
        ?.lt('start_hour', 8)
        ?.gte('started_at', todayStart?.toISOString());
      if ((earlyCount || 0) > 0) {
        const nightAwards = await checkAndGrantAwards(userId, 'NIGHT_AND_DAY', { count: 1 });
        newlyEarned?.push(...nightAwards);
      }
    }

    // VERSE_MASTERED — check if any verse reached phase 7 in this session
    const phasedVerses = sessionData?.phasedVerses || [];
    const masteredCount = phasedVerses?.filter(v => v?.newPhase >= 7 && v?.oldPhase < 7)?.length || 0;
    if (masteredCount > 0) {
      const masteredAwards = await checkAndGrantAwards(userId, 'VERSE_MASTERED', { phase: 7 });
      newlyEarned?.push(...masteredAwards);
    }

    return newlyEarned;
  } catch (err) {
    console.error('recordReviewSession error:', err);
    return [];
  }
}
